import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, ChevronDown } from 'lucide-react'
import ContentIcon from '../common/ContentIcon'
import SmartLink from '../common/SmartLink'
import { fill, useSiteContent } from '../../content/useSiteContent'

const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
  </svg>
)

// Where each social icon goes comes from Site Settings (Instagram, Facebook, Pinterest, YouTube, LinkedIn);
// which icons appear, the link columns, the blurb, copyright and badges are managed in the dashboard
// (Site content → Header & footer).
const SOCIAL_ICONS = {
  instagram: { Icon: Instagram, label: 'Instagram' },
  facebook: { Icon: Facebook, label: 'Facebook' },
  pinterest: { Icon: PinterestIcon, label: 'Pinterest' },
  youtube: { Icon: Youtube, label: 'YouTube' },
  linkedin: { Icon: Linkedin, label: 'LinkedIn' },
}

function socialHref(key, value) {
  const v = String(value || '').trim()
  if (!v) return '#'
  return v
}

export default function Footer() {
  const { site } = useSiteContent()
  const { footer, branding, vars } = site

  // Track accordion state on mobile. All start collapsed for clean initial view.
  const [openSections, setOpenSections] = useState({})

  const toggleSection = (category) => {
    setOpenSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }))
  }

  // /blog and /faqs only appear once there is something published there (Content → Blog / FAQs).
  const hiddenLinks = new Set([
    ...(site.features?.blog ? [] : ['/blog']),
    ...(site.features?.faqs ? [] : ['/faqs']),
  ])

  return (
    <footer className="bg-[#121212] text-white pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-8 sm:pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* ── Brand Column ────────────────────────────────── */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-4 focus:outline-none" aria-label="Rayoptics home">
              <img
                src={branding.footer_logo_mark}
                alt=""
                className="h-8 sm:h-9 w-auto object-contain shrink-0"
              />
              <div className="h-7 sm:h-8 w-px bg-white/20 shrink-0" />
              <img
                src={branding.footer_logo_text}
                alt={site.settings.store_name || 'Rayoptics'}
                className="h-10 sm:h-12 w-auto object-contain brightness-0 invert shrink-0"
              />
            </Link>

            <p className="text-white/70 text-sm leading-relaxed max-w-sm mb-6">
              {fill(footer.tagline, vars)}
            </p>

            {/* Direct Contact info */}
            <div className="space-y-3 text-sm text-white/75 mb-6">
              {vars.phone && (
                <a
                  href={`tel:${vars.phone_tel || vars.phone}`}
                  className="flex items-center gap-3 text-white/80 hover:text-[#5FC03F] transition-colors group"
                >
                  <span className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#5FC03F]/40 group-hover:bg-[#5FC03F]/10 transition-colors">
                    <Phone className="h-4 w-4 text-[#5FC03F]" />
                  </span>
                  <span className="font-medium">{vars.phone}</span>
                </a>
              )}
              {vars.email && (
                <a
                  href={`mailto:${vars.email}`}
                  className="flex items-center gap-3 text-white/80 hover:text-[#5FC03F] transition-colors group"
                >
                  <span className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 group-hover:border-[#5FC03F]/40 group-hover:bg-[#5FC03F]/10 transition-colors">
                    <Mail className="h-4 w-4 text-[#5FC03F]" />
                  </span>
                  <span className="font-medium break-all">{vars.email}</span>
                </a>
              )}
              {vars.address && (
                <div className="flex items-start gap-3 text-white/70">
                  <span className="w-8 h-8 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="h-4 w-4 text-[#5FC03F]" />
                  </span>
                  <span className="leading-snug">{vars.address}</span>
                </div>
              )}
            </div>

            {/* Social channels */}
            <div className="flex items-center gap-2.5 flex-wrap">
              {footer.socials.map((key) => {
                const item = SOCIAL_ICONS[key]
                if (!item) return null
                const { Icon, label } = item
                const href = socialHref(key, site.settings[key])
                return (
                  <a
                    key={label}
                    href={href}
                    {...(href !== '#' ? { target: '_blank', rel: 'noreferrer' } : {})}
                    aria-label={label}
                    className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center text-white/70 hover:text-[#5FC03F] hover:border-[#5FC03F]/50 hover:bg-white/10 active:scale-95 transition-all duration-200"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                )
              })}
            </div>
          </div>

          {/* ── Link Columns: Desktop Grid (md and up) ──────────── */}
          <div className="hidden md:grid col-span-1 md:col-span-2 lg:col-span-3 grid-cols-2 lg:grid-cols-3 gap-8">
            {footer.columns.map(({ title: category, links }) => {
              const visibleLinks = links.filter((link) => !hiddenLinks.has(link.href))
              if (visibleLinks.length === 0) return null

              return (
                <div key={category}>
                  <h4 className="text-white font-semibold text-xs uppercase tracking-wider mb-4 font-montserrat text-white/90">
                    {category}
                  </h4>
                  <ul className="space-y-2.5">
                    {visibleLinks.map((link) => (
                      <li key={link.label}>
                        <SmartLink
                          to={link.href}
                          className="font-montserrat text-white/65 text-sm hover:text-[#5FC03F] transition-colors duration-200 inline-block"
                        >
                          {link.label}
                        </SmartLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          {/* ── Link Columns: Mobile Accordions (below md) ───────── */}
          <div className="md:hidden border-t border-white/10 divide-y divide-white/10 -mt-2">
            {footer.columns.map(({ title: category, links }) => {
              const isOpen = !!openSections[category]
              const visibleLinks = links.filter((link) => !hiddenLinks.has(link.href))
              if (visibleLinks.length === 0) return null

              return (
                <div key={category} className="py-0.5">
                  <button
                    type="button"
                    onClick={() => toggleSection(category)}
                    className="w-full flex items-center justify-between py-3.5 text-left font-montserrat text-sm font-semibold text-white hover:text-[#5FC03F] transition-colors focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <span className="tracking-wide uppercase text-xs text-white/90 font-bold">{category}</span>
                    <ChevronDown
                      className={`h-4 w-4 text-white/50 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-[#5FC03F]' : ''
                      }`}
                    />
                  </button>

                  {isOpen && (
                    <ul className="pb-3.5 pt-0.5 space-y-2.5 pl-1 animate-fade-in">
                      {visibleLinks.map((link) => (
                        <li key={link.label}>
                          <SmartLink
                            to={link.href}
                            className="block py-1 text-sm text-white/65 hover:text-[#5FC03F] active:text-[#5FC03F] transition-colors font-montserrat"
                          >
                            {link.label}
                          </SmartLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Trust signals & Badges ──────────────────────────── */}
        {footer.badges && footer.badges.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center justify-center md:justify-start gap-2.5 sm:gap-3">
            {footer.badges.map((b) => (
              <div
                key={b.text}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs text-white/80"
              >
                <ContentIcon name={b.icon} className="h-3.5 w-3.5 text-[#5FC03F] shrink-0" />
                <span className="font-medium">{fill(b.text, vars)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Bottom Copyright Bar ─────────────────────────────── */}
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50 text-center sm:text-left">
          <p className="font-montserrat leading-relaxed">
            {fill(footer.copyright, vars)}
            {site.store?.gstin ? <span className="ml-2 inline-block">· GSTIN {site.store.gstin}</span> : null}
          </p>
          <p className="text-white/40 text-[11px] font-montserrat tracking-tight">
            Designed for crystal-clear clarity & style
          </p>
        </div>
      </div>
    </footer>
  )
}
